// frontend/src/lib/flipOrder.ts
import type { PlacementFlip } from './offlineStore'

/** A flip is retried this many times before it is given up (the organizer can place the person in O1). */
export const FLIP_MAX_RETRIES = 5

/** Not yet on the server and still retryable: visits for this person must wait for it. */
export function isFlipOutstanding(f: Pick<PlacementFlip, 'synced' | 'retryCount'>): boolean {
  return !f.synced && f.retryCount < FLIP_MAX_RETRIES
}

/**
 * [P4 final-review I-3] The server decides a visit's WorksiteId from the member's CURRENT placement. A
 * visit for someone with an unsynced "Visit at home today" flip must therefore reach the server AFTER
 * the flip, or a home visit is stamped with the worksite. The flip's own NotAtWork visit is the
 * exception: it must go BEFORE the flip (that one is meant to stamp the worksite).
 * Returns a predicate: true = hold this visit until the person's flip has synced.
 */
export function heldBehindFlip(
  flips: Pick<PlacementFlip, 'contactId' | 'afterClientVisitId' | 'synced' | 'retryCount'>[],
): (v: { contactId: string; clientVisitId?: string | null }) => boolean {
  const outstanding = flips.filter(isFlipOutstanding)
  if (outstanding.length === 0) return () => false
  const ownVisits = new Set(outstanding.map(f => f.afterClientVisitId))
  const contacts = new Set(outstanding.map(f => f.contactId))
  return v => contacts.has(v.contactId) && !(v.clientVisitId != null && ownVisits.has(v.clientVisitId))
}
